import React, { useEffect, useMemo, useRef, useState } from "react";
// PersonAddOutlined rather than PersonAddAltOutlined — the "Alt" variant was
// added in a later @mui/icons-material release and breaks earlier 5.x installs.
import { PersonAddOutlined, RefreshOutlined } from "@mui/icons-material";
import { Drawer } from "antd";

import { formatDate, getFullName, getInitials, getStatus } from "../adminUtils";
import ErrorMessage from "./ErrorMessage";
import RecordForm from "./RecordForm";
import RecordRow from "./RecordRow";
import Topbar from "./Topbar";

const SectionHeading = ({ children }) => (
  <div style={{ borderBottom: "2px solid #f0f0f0", color: "#344054", fontSize: 12, fontWeight: 800, letterSpacing: "0.06em", marginBottom: 14, paddingBottom: 6, textTransform: "uppercase" }}>
    {children}
  </div>
);

const DetailField = ({ label, value, isStatus, full }) => {
  if (value == null || value === "" || value === "-") return null;
  return (
    <div style={{ gridColumn: full ? "1 / -1" : undefined }}>
      <div style={{ color: "#667085", fontSize: 11, fontWeight: 700, letterSpacing: "0.04em", marginBottom: 3, textTransform: "uppercase" }}>
        {label}
      </div>
      {isStatus
        ? <span className={`admin-pill admin-pill-${value}`}>{value}</span>
        : <div style={{ color: "#101828", fontSize: 14, fontWeight: 600, wordBreak: "break-word" }}>{value}</div>
      }
    </div>
  );
};

const AdminTablePage = ({
  type,
  title,
  subtitle,
  records,
  fields,
  defaultForm,
  loading,
  error,
  onCreate,
  onUpdate,
  onDelete,
  onRefresh,
  page = 1,
  pageSize = 10,
  total = 0,
  onPageChange,
  onSearch,
  onSetPaused,
  currentAdminEmail,
  currentAdminId,
}) => {
  const [query, setQuery] = useState("");
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [formValues, setFormValues] = useState(defaultForm);
  const [saving, setSaving] = useState(false);
  const [detailUser, setDetailUser] = useState(null);

  useEffect(() => {
    setIsFormOpen(false);
    setEditingRecord(null);
    setFormValues(defaultForm);
    setQuery("");
  }, [type, defaultForm]);

  // Debounced server-side search: when onSearch is provided, the query is sent
  // to the backend (which searches across ALL records, not just the loaded
  // page). Skips the first render so we don't refetch on mount.
  const onSearchRef = useRef(onSearch);
  onSearchRef.current = onSearch;
  const isFirstSearchRender = useRef(true);
  useEffect(() => {
    if (!onSearchRef.current) return undefined;
    if (isFirstSearchRender.current) {
      isFirstSearchRender.current = false;
      return undefined;
    }
    const handle = setTimeout(() => {
      onSearchRef.current(query.trim());
    }, 400);
    return () => clearTimeout(handle);
  }, [query]);

  const filteredRecords = useMemo(() => {
    const search = query.trim().toLowerCase();
    if (!search) return records;
    return records.filter((record) => {
      const roleStr =
        typeof record.role === "object"
          ? record.role?.title || record.role?.name || ""
          : record.role || "";
      const text = [getFullName(record), record.email, roleStr, record.username]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return text.includes(search);
    });
  }, [query, records]);

  // With server-side search, `records` is already the filtered page from the
  // backend, so don't filter again on the client.
  const displayRecords = onSearch ? records : filteredRecords;

  const openCreateForm = () => {
    setEditingRecord(null);
    setFormValues(defaultForm);
    setIsFormOpen(true);
  };

  const openEditForm = (record) => {
    setEditingRecord(record);
    const merged = { ...defaultForm, ...record };
    const sanitized = Object.fromEntries(
      Object.entries(merged).map(([key, val]) => [
        key,
        val !== null && typeof val === "object"
          ? val?.title || val?.name || val?.value || ""
          : val,
      ])
    );
    setFormValues({ ...sanitized, password: "" });
    setIsFormOpen(true);
  };

  const closeForm = () => {
    setIsFormOpen(false);
    setEditingRecord(null);
    setFormValues(defaultForm);
  };

  const handleSubmit = async () => {
    setSaving(true);
    const payload = { ...formValues };
    if (editingRecord && !payload.password) delete payload.password;

    try {
      if (editingRecord) {
        await onUpdate(editingRecord.id || editingRecord._id, payload);
      } else {
        await onCreate(payload);
      }
      closeForm();
    } finally {
      setSaving(false);
    }
  };

  const totalPages = total > 0 ? Math.ceil(total / pageSize) : 0;

  return (
    <>
      <Topbar title={title} subtitle={subtitle}>
        <button className="admin-btn admin-btn-ghost" onClick={onRefresh} type="button">
          <RefreshOutlined fontSize="small" />
          Refresh
        </button>
        <button className="admin-btn admin-btn-primary" onClick={openCreateForm} type="button">
          <PersonAddOutlined fontSize="small" />
          Add {type === "users" ? "User" : "Admin"}
        </button>
      </Topbar>
      <ErrorMessage message={error} />
      <RecordForm
        editing={Boolean(editingRecord)}
        fields={fields}
        formValues={formValues}
        onCancel={closeForm}
        onChange={(name, value) =>
          setFormValues((current) => ({ ...current, [name]: value }))
        }
        onSave={handleSubmit}
        open={isFormOpen}
        saving={saving}
        title={`${editingRecord ? "Edit" : "Add"} ${
          type === "users" ? "User" : "Admin"
        }`}
      />
      <div className="admin-filter-row">
        <input
          className="admin-input"
          onChange={(event) => setQuery(event.target.value)}
          placeholder={`Search ${type}`}
          type="search"
          value={query}
        />
      </div>
      <div className="admin-table-wrap">
        <div className="admin-table-scroll">
          <table className="admin-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                {type === "users" ? <th>Balance</th> : <th>Role</th>}
                <th>Joined</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="5">
                    <div className="admin-empty">
                      <p>Loading records...</p>
                    </div>
                  </td>
                </tr>
              ) : displayRecords.length === 0 ? (
                <tr>
                  <td colSpan="5">
                    <div className="admin-empty">
                      <p>No records found.</p>
                    </div>
                  </td>
                </tr>
              ) : (
                displayRecords.map((record) => (
                  <RecordRow
                    key={record.id || record._id || record.email}
                    isSelf={
                      type === "admins" &&
                      ((!!currentAdminId &&
                        String(record.id || record._id) ===
                          String(currentAdminId)) ||
                        (!!currentAdminEmail &&
                          record.email === currentAdminEmail))
                    }
                    onDelete={onDelete}
                    onEdit={openEditForm}
                    onSetPaused={type === "users" ? onSetPaused : undefined}
                    onViewDetail={type === "users" ? setDetailUser : undefined}
                    record={record}
                    type={type}
                  />
                ))
              )}
            </tbody>
          </table>
        </div>
        {totalPages > 1 && onPageChange && (
          <div className="admin-pagination">
            <span className="admin-pagination-info">
              Showing {(page - 1) * pageSize + 1}–{Math.min(page * pageSize, total)} of {total} records
            </span>
            <div className="admin-pagination-controls">
              <button
                className="admin-pagination-btn"
                disabled={page <= 1 || loading}
                onClick={() => onPageChange(page - 1)}
                type="button"
              >
                ← Prev
              </button>
              <span className="admin-pagination-page">
                Page {page} of {totalPages}
              </span>
              <button
                className="admin-pagination-btn"
                disabled={page >= totalPages || loading}
                onClick={() => onPageChange(page + 1)}
                type="button"
              >
                Next →
              </button>
            </div>
          </div>
        )}
      </div>

      {/* ── User Detail Drawer ── */}
      <Drawer
        title={
          detailUser ? (
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <div style={{
                alignItems: "center", background: "#1cc400", borderRadius: "50%",
                color: "#fff", display: "inline-flex", fontSize: 18, fontWeight: 800,
                height: 42, justifyContent: "center", width: 42, flexShrink: 0,
              }}>
                {detailUser ? getInitials(detailUser) : ""}
              </div>
              <div>
                <div style={{ fontWeight: 700, fontSize: 16 }}>{getFullName(detailUser)}</div>
                <div style={{ color: "#667085", fontSize: 13, fontWeight: 400 }}>{detailUser.email}</div>
              </div>
            </div>
          ) : null
        }
        open={!!detailUser}
        onClose={() => setDetailUser(null)}
        placement="right"
        width={520}
        destroyOnClose
      >
        {detailUser && (
          <div style={{ display: "flex", flexDirection: "column", gap: 0 }}>

            {/* ── Personal Info ── */}
            <SectionHeading>Personal Info</SectionHeading>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px 24px", marginBottom: 24 }}>
              <DetailField label="First Name"  value={detailUser.firstName} />
              <DetailField label="Last Name"   value={detailUser.lastName} />
              <DetailField label="Email"       value={detailUser.email} full />
              <DetailField label="Phone"       value={detailUser.phoneNumber} />
              <DetailField label="City"        value={detailUser.city} />
              <DetailField label="State"       value={detailUser.state} />
              <DetailField label="Street"      value={detailUser.street} />
              <DetailField label="Street No."  value={detailUser.streetNumber} />
              <DetailField label="Bio"         value={detailUser.userBio} full />
            </div>

            {/* ── Account Info ── */}
            <SectionHeading>Account</SectionHeading>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px 24px", marginBottom: 24 }}>
              <DetailField label="Balance"     value={detailUser.balance != null ? `$${detailUser.balance}` : null} />
              <DetailField label="Joined"      value={formatDate(detailUser.createdAt)} />
              <DetailField label="Status"      value={getStatus(detailUser)} isStatus />
              <DetailField label="Email Verified" value={detailUser.emailVerify ? "Yes" : "No"} />
              <DetailField label="Service Reg."   value={detailUser.isServiceRegisted ? "Yes" : "No"} />
              <DetailField label="SMS Enabled"    value={detailUser.smsEnabled ? "Yes" : "No"} />
              <DetailField label="Notifications"  value={detailUser.notification ? "On" : "Off"} />
            </div>

            {/* ── Services ── */}
            <SectionHeading>Services ({detailUser.extra?.length ?? 0})</SectionHeading>
            <div style={{ marginBottom: 24 }}>
              {detailUser.extra?.length > 0 ? (
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  {detailUser.extra.map((s, i) => (
                    <div key={i} style={{ background: "#f9f9f9", borderRadius: 8, padding: "10px 14px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <div>
                        <div style={{ fontWeight: 700, fontSize: 13, color: "#101828" }}>{s.title || s.name || `Service ${i + 1}`}</div>
                        {s.category?.title && <div style={{ fontSize: 12, color: "#667085" }}>{s.category.title}</div>}
                      </div>
                      {s.status && <span className={`admin-pill admin-pill-${s.status?.toLowerCase()}`}>{s.status}</span>}
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{ color: "#667085", fontSize: 13 }}>No services found.</div>
              )}
            </div>

            {/* ── Reviews ── */}
            <SectionHeading>Reviews ({detailUser.receivedReviews?.length ?? 0})</SectionHeading>
            <div style={{ marginBottom: 24 }}>
              {detailUser.receivedReviews?.length > 0 ? (
                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                  {detailUser.receivedReviews.map((r, i) => (
                    <div key={i} style={{ background: "#f9f9f9", borderRadius: 8, padding: "10px 14px" }}>
                      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                        <span style={{ fontWeight: 700, fontSize: 13, color: "#101828" }}>
                          {r.reviewer?.firstName || r.reviewerName || "User"}
                        </span>
                        <span style={{ fontSize: 13, color: "#f59e0b", fontWeight: 700 }}>
                          {"★".repeat(r.rating || 0)}{"☆".repeat(5 - (r.rating || 0))}
                        </span>
                      </div>
                      {r.comment && <p style={{ fontSize: 13, color: "#667085", margin: 0 }}>{r.comment}</p>}
                      <span style={{ fontSize: 11, color: "#aaa" }}>{formatDate(r.createdAt)}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{ color: "#667085", fontSize: 13 }}>No reviews found.</div>
              )}
            </div>

          </div>
        )}
      </Drawer>
    </>
  );
};

export default AdminTablePage;
